'use client'

import { useRef } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import { Badge } from '@mdxui/primitives'
import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import {
  sectionVariants,
  headerVariants,
  gridContainerVariants,
  gridItemVariants,
  reducedMotionVariants,
} from '../../lib/motion'
import { CodeWindowBase } from '../code-window'

/** Single step in the how-it-works flow */
export interface HowItWorksCardsStep {
  /** Step number displayed as badge (e.g., "01", "02") */
  number: string
  title: string
  /** Step description - supports Markdown (inline code, links, bold, etc.) */
  description: string
  code: { code: string; language: string }
}

export interface HowItWorksCardsProps {
  /** Section id for anchor links */
  id?: string
  /** Badge text */
  badge?: string
  /** Section heading */
  heading?: string
  /** Section description */
  description?: string
  /** Array of steps (3) */
  steps?: HowItWorksCardsStep[]
  className?: string
}

const defaultSteps: HowItWorksCardsStep[] = [
  {
    number: '01',
    title: 'Install the package',
    description:
      'Add MDXUI to your project with a single command. Works with `npm`, `yarn`, `pnpm`, or `bun`.',
    code: {
      code: `npm install mdxui`,
      language: 'bash',
    },
  },
  {
    number: '02',
    title: 'Import components',
    description:
      'Import the components you need from the library. Full TypeScript support included.',
    code: {
      code: `import { Hero, Features } from 'mdxui'`,
      language: 'typescript',
    },
  },
  {
    number: '03',
    title: 'Build your page',
    description:
      'Compose components together to create beautiful interfaces. Customize as needed.',
    code: {
      code: `export default function Page() {
  return (
    <>
      <Hero title="Welcome" />
      <Features features={[...]} />
    </>
  )
}`,
      language: 'typescript',
    },
  },
]

/**
 * HowItWorksCards - Numbered steps grid with code windows
 * 3-column grid with numbered badges, titles, descriptions, and code snippets
 */
const HowItWorksCards = ({
  id,
  badge = 'How it works',
  heading = 'Empower your business with our solutions',
  description = 'Discover how our tools can transform your workflow and help you achieve your goals faster.',
  steps = defaultSteps,
  className,
}: HowItWorksCardsProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const headerAnimation = prefersReducedMotion ? reducedMotionVariants : headerVariants
  const itemAnimation = prefersReducedMotion ? reducedMotionVariants : gridItemVariants

  return (
    <motion.section
      id={id}
      ref={sectionRef}
      className={cn('py-16 sm:py-24 lg:py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          className="flex flex-col items-center pb-8 text-center"
          variants={headerAnimation}
        >
          <span className="mb-6 flex items-center justify-center">
            <Badge variant="outline" className="bg-background px-3 py-1">
              {badge}
            </Badge>
          </span>
          <h2 className="pb-4 text-3xl font-semibold sm:text-4xl text-balance">
            {heading}
          </h2>
          <div className="max-w-md lg:max-w-2xl">
            <Markdown size="base">{description}</Markdown>
          </div>
        </motion.div>

        {/* Steps Grid */}
        <motion.div
          className="mt-8 grid auto-rows-fr grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
          variants={gridContainerVariants(0.1, 0.2)}
        >
          {steps.map((step) => (
            <motion.div
              key={step.number}
              className="flex flex-col overflow-hidden rounded-[var(--radius-xl,calc(var(--radius)*1.5))] border border-border bg-background shadow-sm"
              variants={itemAnimation}
            >
              <div className="bg-card flex-1 p-4 sm:p-6 border-b border-border/50">
                <Badge variant="outline" className="rounded-[var(--radius-sm,calc(var(--radius)*0.5))] bg-muted/50 py-2 font-mono">
                  {step.number}
                </Badge>
                <p className="my-4 text-lg font-semibold">{step.title}</p>
                <div className="prose prose-sm max-w-none text-muted-foreground prose-strong:text-foreground prose-code:rounded-[var(--radius-sm,calc(var(--radius)*0.5))] prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:font-normal prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none prose-a:text-primary prose-a:no-underline hover:prose-a:underline">
                  <ReactMarkdown>{step.description}</ReactMarkdown>
                </div>
              </div>
              <div className="flex min-h-[180px] flex-col">
                <CodeWindowBase
                  code={step.code.code}
                  language={step.code.language}
                  showHeader={false}
                  showCopy={false}
                  theme="dark"
                  style="borderless"
                  className="h-full min-h-0 flex-1 rounded-t-none border-x-0 border-b-0"
                  contentClassName="h-full"
                />
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  )
}

export { HowItWorksCards }
